<script setup lang="ts">
/**
 * Right cell for the mobile clients table.
 *
 * Renders the client's balance (sign-colored monospace), last-visit info
 * (duration + absolute date, freshness-colored like LastVisitCell), and a
 * small visit-count chip when the count is positive. All content is
 * right-aligned to contrast with the name/meta block in the left cell.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoney } from '@/utils/formatMoney'
import { formatStartDate } from '@/utils/formatStartDate'
import { visitFreshness } from '@/config/visits'
import { MS_PER_DAY } from '@/utils/time'

const props = withDefaults(
  defineProps<{
    balance?: number | null
    /** Debt in minor units, derived from `latest_balance` (see `DebtCell`). */
    debt?: number | null
    lastVisit?: string
    businessServiceCount?: number | null
    width?: string
  }>(),
  {},
)

const { t } = useI18n()

const hasBalance = computed(
  () => props.balance !== undefined && props.balance !== null && !Number.isNaN(props.balance),
)

const balanceResult = computed(() =>
  hasBalance.value ? formatMoney({ amount: props.balance! }) : null,
)

const balanceStyle = computed(() => {
  if (!hasBalance.value) return {}
  const raw = props.balance!
  if (raw < 0) return { color: 'var(--color-money-negative)' }
  if (raw > 0) return { color: 'var(--color-money-positive)' }
  return { color: 'var(--color-tufte-muted)' }
})

const hasDebt = computed(
  () => props.debt !== undefined && props.debt !== null && !Number.isNaN(props.debt),
)

// Mirrors `DebtCell`: a non-negative `debt` (latest_balance) means last
// month's debt is closed, so it always renders as 0/green — never the raw
// positive value.
const isDebt = computed(() => hasDebt.value && props.debt! < 0)

const debtResult = computed(() =>
  hasDebt.value ? formatMoney({ amount: isDebt.value ? props.debt! : 0 }) : null,
)

const debtStyle = computed(() => {
  if (!hasDebt.value) return {}
  return isDebt.value
    ? { color: 'var(--color-money-negative)' }
    : { color: 'var(--color-money-positive)' }
})

const hasLastVisit = computed(
  () => typeof props.lastVisit === 'string' && props.lastVisit.trim() !== '',
)

const visitResult = computed(() =>
  hasLastVisit.value ? formatStartDate({ date: props.lastVisit! }) : null,
)

const freshnessClass = computed(() => {
  if (!hasLastVisit.value || !props.lastVisit) return ''
  const parsed = new Date(props.lastVisit)
  if (Number.isNaN(parsed.getTime())) return ''
  const daysAgo = (Date.now() - parsed.getTime()) / MS_PER_DAY
  if (daysAgo <= visitFreshness.recentDays) return 'visit-cell--recent'
  if (daysAgo <= visitFreshness.staleDays) return 'visit-cell--stale'
  return 'visit-cell--lost'
})

const showVisitsChip = computed(
  () =>
    typeof props.businessServiceCount === 'number' &&
    Number.isFinite(props.businessServiceCount) &&
    props.businessServiceCount > 0,
)
</script>

<template>
  <td class="client-mobile-right font-tufte text-tufte-text">
    <div class="right-layout">
      <div class="money-row">
        <div class="money-col">
          <span class="money-label text-tufte-muted">{{ t('common.balance') }}</span>
          <span v-if="balanceResult" class="money-value" :style="balanceStyle">
            {{ balanceResult.formatted }}
          </span>
          <span v-else class="money-value text-tufte-muted">–</span>
        </div>
        <div class="money-col">
          <span class="money-label text-tufte-muted">{{ t('common.debt') }}</span>
          <span v-if="debtResult" class="money-value" :style="debtStyle">
            {{ debtResult.formatted }}
          </span>
          <span v-else class="money-value text-tufte-muted">–</span>
        </div>
      </div>

      <template v-if="visitResult">
        <span class="visit-duration" :class="freshnessClass">
          {{ visitResult.durationSince }}
        </span>
        <span class="visit-date text-tufte-muted">{{ visitResult.formattedDate }}</span>
      </template>
      <span v-else class="visit-duration text-tufte-muted">{{ t('common.noVisits') }}</span>

      <span v-if="showVisitsChip" class="visits-chip text-tufte-muted">
        {{ t('units.visit', businessServiceCount ?? 0) }}
      </span>
    </div>
  </td>
</template>

<style scoped>
.client-mobile-right {
  vertical-align: top;
}

.right-layout {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
}

.money-row {
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
}

.money-col {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.money-label {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: var(--line-height-tufte-normal);
}

.money-value {
  font-family: var(--font-tufte-mono);
  /* Mono digits at the same rem size as the sans-serif visit-duration/date
   * text below still read visually larger (taller cap-height), so the size
   * is nudged down to match — same compensation ClientCard's ledger table
   * applies to its Δ/Bal. columns. */
  font-size: calc(var(--text-tufte-xs) - 0.05rem);
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.visit-duration,
.visit-date {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  line-height: var(--line-height-tufte-normal);
}

.visits-chip {
  display: inline-block;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  border: 1px solid var(--color-tufte-border);
  border-radius: 999px;
  padding: 0.05rem 0.4rem;
  margin-top: 0.15rem;
  line-height: var(--line-height-tufte-normal);
}
</style>
